import { useCallback } from 'react';
import { Subject } from 'rxjs/internal/Subject';
import { useBooleanValue } from '@edikk-edikk/hooks';
import loadable from '@loadable/component';
import { useMenuActionsBlocker } from '#baseMenu/hooks/use-menu-actions-blocker';
import { useAppDispatch } from '#components/redux/hooks';
import { useMenuState } from '#baseMenu/hooks/use-menu-state';
import { setIsContentLoaded, setIsContentLoadInProcess } from '#baseMenu/reducers/slice-menu';
import { useMenuActionControlProviderContext } from '#baseMenu/hooks/use-menu-action-control-provider-context';

const useMenuContentVisibleControl = (
  Content: ReturnType<typeof loadable>,
  openSubject: Subject<void>,
  hideSubject: Subject<void>,
) => {
  const [isOpen, setIsOpenAsTrue, setIsOpenAsFalse] = useBooleanValue();
  const { blockActions, unblockActions } = useMenuActionsBlocker();
  const { isContentLoaded } = useMenuState();
  const dispatch = useAppDispatch();
  const { menuActionControl } = useMenuActionControlProviderContext();

  const hideForce = useCallback((): Promise<void> => new Promise<void>((resolve) => {
    blockActions();

    setIsOpenAsFalse();

    const subscription = hideSubject.subscribe(() => {
      unblockActions();

      resolve();

      subscription.unsubscribe();
    });
  }), [blockActions, hideSubject, setIsOpenAsFalse, unblockActions]);

  const hide = useCallback((): Promise<void> => new Promise<void>((resolve) => {
    if (!isOpen) {
      resolve();
      return;
    }

    hideForce();

    menuActionControl.remove('menuContent');
    resolve();
  }), [menuActionControl, hideForce, isOpen]);

  const open = useCallback(
    () => {
      blockActions();

      if (isContentLoaded) {
        setIsOpenAsTrue();

        menuActionControl.add({ id: 'menuContent', hide: hideForce });
      } else {
        dispatch(setIsContentLoadInProcess(true));
        Content.load().then(() => {
          dispatch(setIsContentLoaded(true));
          dispatch(setIsContentLoadInProcess(false));

          setIsOpenAsTrue();

          menuActionControl.add({ id: 'menuContent', hide: hideForce });
        });
      }

      const subscription = openSubject.subscribe(() => {
        unblockActions();

        subscription.unsubscribe();
      });
    },
    [
      Content,
      blockActions,
      dispatch,
      hideForce,
      isContentLoaded,
      menuActionControl,
      openSubject,
      setIsOpenAsTrue,
      unblockActions,
    ],
  );

  return { isOpen, open, hide };
};

export { useMenuContentVisibleControl };
